import { defineStore } from 'pinia';
import { type Quote, defaultQuote } from '../models/quote';
import { Firestore, collection, doc, getDocs, setDoc, deleteDoc, addDoc } from 'firebase/firestore';

export interface QuoteStoreState {
	quotes: Quote[];
	loaded: boolean;
	selectedQuote: Quote | null;
	openDialogQuote: boolean;
}

export const useQuoteStore = defineStore('QuoteStore', {
	state: (): QuoteStoreState => ({
		quotes: [],
		loaded: false,
		selectedQuote: null,
		openDialogQuote: false,
	}),

	getters: {
		randomQuote: (state): Quote | null => {
			if (state.quotes.length === 0) return null;
			return state.quotes[Math.floor(Math.random() * state.quotes.length)];
		},
	},

	actions: {
		async fetchAll() {
			if (this.loaded) return;
			const { $firestore } = useNuxtApp();
			const snap = await getDocs(collection($firestore as Firestore, 'quotes'));
			this.quotes = snap.docs.map(d => ({ ...d.data(), id: d.id } as Quote));
			this.loaded = true;
		},

		async upsert(quote: Quote) {
			const { $firestore } = useNuxtApp();
			if (quote.id) {
				await setDoc(doc($firestore as Firestore, 'quotes', quote.id), {
					text: quote.text,
					author: quote.author,
					imageUrl: quote.imageUrl || '',
					source: quote.source || 'custom',
				});
				const idx = this.quotes.findIndex(q => q.id === quote.id);
				if (idx !== -1) this.quotes[idx] = quote;
				else this.quotes.push(quote);
			} else {
				const docRef = await addDoc(collection($firestore as Firestore, 'quotes'), {
					text: quote.text,
					author: quote.author,
					imageUrl: quote.imageUrl || '',
					source: quote.source || 'custom',
				});
				quote.id = docRef.id;
				this.quotes.push(quote);
			}
		},

		async remove(quoteId: string) {
			const { $firestore } = useNuxtApp();
			await deleteDoc(doc($firestore as Firestore, 'quotes', quoteId));
			this.quotes = this.quotes.filter(q => q.id !== quoteId);
		},

		showDialog(quote?: Quote) {
			this.selectedQuote = quote || defaultQuote();
			this.openDialogQuote = true;
		},

		hideDialog() {
			this.openDialogQuote = false;
			this.selectedQuote = null;
		},
	},
});
